import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Button from "components/Button/Regular";
import Input from "components/Input/Input";
import Select from "components/Input/Select";
import vgToast from "utils/toast";
import {
  PAYMENT_TYPE,
  paymentTypeOptions,
  recordPayment,
} from "services/payments.service";

type Props = {
  open: boolean;
  orgId: number;
  invoiceId: number;
  contactId?: number;
  /** What is still owed, prefilled as the amount. */
  outstanding?: number;
  currency?: number;
  onCancel: () => void;
  onRecorded: () => void;
};

/**
 * Records a payment against an invoice.
 *
 * The amount is prefilled with what is outstanding, since paying the balance is
 * the common case, but it stays editable - part payments are the reason a
 * payment is a row rather than a flag.
 */
const RecordPayment = ({
  open,
  orgId,
  invoiceId,
  contactId,
  outstanding,
  currency,
  onCancel,
  onRecorded,
}: Props) => {
  const { getAccessTokenSilently } = useAuth0();

  const [amount, setAmount] = React.useState("");
  const [paymentType, setPaymentType] = React.useState(String(PAYMENT_TYPE.CARD));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setAmount(outstanding != null && outstanding > 0 ? String(outstanding) : "");
    setPaymentType(String(PAYMENT_TYPE.CARD));
    setError(null);
  }, [open, outstanding]);

  if (!open) return null;

  const value = Number(amount);
  const overpaying = outstanding != null && value > outstanding;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!contactId) {
      setError("This invoice has no client, so there is nobody to record a payment for.");
      return;
    }

    setError(null);
    setBusy(true);
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await recordPayment(token, {
        orgId,
        invoiceId,
        contactId,
        amount: value,
        currency: currency ?? 0,
        paymentType: Number(paymentType),
      });

      if (apiError) {
        setError(apiError.message);
        return;
      }

      vgToast.success("Payment recorded.");
      onRecorded();
    } catch (err) {
      vgToast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 print:hidden">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-slate-900/35 backdrop-blur-[2px]"
        onClick={() => {
          if (!busy) onCancel();
        }}
      />

      <form
        onSubmit={submit}
        className="relative max-h-full w-full max-w-md overflow-y-auto rounded-3xl border border-slate-200 bg-white shadow-[0_18px_50px_-12px_rgba(15,23,42,0.28)]"
      >
        <div className="border-b border-slate-100 bg-slate-50/70 px-6 py-5">
          <h3 className="text-lg font-semibold text-slate-900">Record a payment</h3>
          <p className="mt-1 text-sm leading-6 text-slate-600">
            {outstanding != null
              ? `${outstanding.toFixed(2)} is outstanding on this invoice.`
              : "Records what the client has paid against this invoice."}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 px-6 py-5">
          <Input
            type="number"
            label="Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />

          <Select
            label="Paid by"
            options={paymentTypeOptions}
            value={paymentType}
            onChange={(e) => setPaymentType(e.target.value)}
          />

          {overpaying && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              That is more than is outstanding. The invoice will show a credit.
            </p>
          )}

          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <Button color="Secondary" onClick={onCancel} disabled={busy}>
            <span>Cancel</span>
          </Button>
          <Button color="Primary" type="submit" disabled={busy}>
            <span>{busy ? "Recording…" : "Record payment"}</span>
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RecordPayment;
